// src/pages/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Draggable from "react-draggable";
import { motion } from "framer-motion";
import API from "../utils/api";
import FloatingNotice from "../components/FloatingNotice";
import ReferralWithdrawalCard from "../components/ReferralWithdrawalCard";
import { useCountry } from "../context/CountryContext";

// 🇳🇬 Nigerian logos
import opayLogo from "../assets/opay.png";
import palmpayLogo from "../assets/palmpay.png";
import kudaLogo from "../assets/kuda.png";
import moniepointLogo from "../assets/moniepoint.png";

// Crypto logos (used by both)
import binanceLogo from "../assets/binance.png";
import bybitLogo from "../assets/bybit.png";
import lunoLogo from "../assets/luno.png";
import paypalLogo from "../assets/paypal.png";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";
const TAWK_SRC = "https://embed.tawk.to/68deec7532ab2019572628de/1j6jcnbu7";

// ----- Tawk Chat Button — now a FIXED FLOATING button at bottom-right -----
const TawkFloatingButton = () => {
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    if (document.getElementById("tawk-dashboard-script")) return;

    const s1 = document.createElement("script");
    s1.id = "tawk-dashboard-script";
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");

    s1.onload = () => {
      if (window.Tawk_API && window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget();
      }
    };
    s1.onerror = () => {
      setStatusMsg("⚠️ Could not load support chat.");
    };

    document.body.appendChild(s1);
  }, []);

  const openTawkChat = () => {
    if (!window.Tawk_API || typeof window.Tawk_API.maximize !== "function") {
      setStatusMsg("⚠️ Support chat isn't ready yet. Please wait a moment.");
      setTimeout(() => setStatusMsg(""), 4000);
      return;
    }
    try {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
      setStatusMsg("");
    } catch (err) {
      setStatusMsg(`⚠️ Error: ${err.message}`);
      setTimeout(() => setStatusMsg(""), 5000);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {statusMsg && (
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-black/90 text-white text-xs rounded-lg whitespace-nowrap shadow-lg max-w-xs">
          {statusMsg}
        </div>
      )}
      <button
        onClick={openTawkChat}
        className="px-5 py-3 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-black font-semibold rounded-full shadow-2xl transform active:scale-95 transition"
      >
        Talk to Support
      </button>
    </div>
  );
};

// Telegram bot banner
const TelegramBotBanner = ({ theme, onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`mb-5 p-4 rounded-2xl bg-gradient-to-r ${theme.primaryGradient} bg-opacity-10 border border-white/10 flex items-center justify-between gap-3 flex-wrap`}
    style={{ backgroundColor: "rgba(59, 130, 246, 0.08)" }}
  >
    <div className="flex items-start gap-3">
      <span className="text-2xl">🤖</span>
      <div>
        <p className="font-semibold text-white text-sm">Need help? Chat with our Telegram assistant</p>
        <p className="text-xs text-gray-300 mt-0.5">
          Register, check your balance, buy coins, and get answers instantly — anytime you need it.
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r ${theme.primaryGradient} text-black whitespace-nowrap`}
      >
        Open Telegram Bot
      </a>
      <button
        onClick={onDismiss}
        className="text-gray-400 hover:text-white text-lg leading-none px-1"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  </motion.div>
);

export default function Dashboard() {
  const { isGhana, isNigeria, config } = useCountry();

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [showPlanPopup, setShowPlanPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [telegramBannerVisible, setTelegramBannerVisible] = useState(true);
  // 🔬 TEMPORARY DEBUG — surfaces the real fetch error on screen instead
  // of it being silently swallowed, which is what left this page stuck
  // on "Loading dashboard..." forever with no visible cause. Remove
  // once the real error is found and fixed.
  const [loadError, setLoadError] = useState("");

  const navigate = useNavigate();

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        bgGradient: "from-[#1a0a00] via-[#0a0500] to-black",
        nameColor: "text-yellow-300",
        balanceGradient: "from-yellow-400 to-red-400",
        primaryGradient: "from-yellow-400 to-red-500",
        secondaryGradient: "from-red-500 to-green-500",
        supportBtn: "from-green-400 to-green-600 hover:from-green-500 hover:to-green-700",
        addMoneyBtn: "from-yellow-500 to-red-600 hover:scale-105",
        subscribeBtn: "from-red-500 to-yellow-500 hover:scale-105",
        accentText: "text-yellow-200",
        currencySymbol: config.currencySymbol,
        appName: "ProBank Ghana",
        flag: "🇬🇭",
        wallets: [
          { name: "MTN MoMo", emoji: "📱", color: "from-yellow-500 to-yellow-700", link: "/wallet", active: true },
          { name: "Telecel Cash", emoji: "💳", color: "from-red-500 to-red-700", link: "/wallet-palmpay", active: true },
          { name: "AirtelTigo", emoji: "💰", color: "from-blue-500 to-purple-600", link: "/wallet-kuda", active: true },
          { name: "GCB Bank", emoji: "🏦", color: "from-green-500 to-emerald-700", link: "/moniepoint", active: true },
        ],
        referralDomain: "prorealalert.com",
      }
    : {
        bgGradient: "from-[#020617] via-[#071032] to-[#03040b]",
        nameColor: "text-sky-300",
        balanceGradient: "from-sky-400 to-blue-300",
        primaryGradient: "from-sky-500 to-indigo-600",
        secondaryGradient: "from-blue-500 to-blue-700",
        supportBtn: "from-green-400 to-green-600 hover:from-green-500 hover:to-green-700",
        addMoneyBtn: "from-sky-500 to-indigo-600 hover:scale-105",
        subscribeBtn: "from-blue-500 to-blue-700 hover:scale-105",
        accentText: "text-sky-200",
        currencySymbol: config.currencySymbol,
        appName: "ProBank",
        flag: "🇳🇬",
        wallets: [
          { name: "Opay", logo: opayLogo, link: "/wallet", active: true },
          { name: "PalmPay", logo: palmpayLogo, link: "/wallet-palmpay", active: true },
          { name: "Kuda", logo: kudaLogo, link: "/wallet-kuda", active: true },
          { name: "MoniePoint", logo: moniepointLogo, link: "/moniepoint", active: true },
        ],
        referralDomain: "prorealalert.com",
      };

  // ----- Fetch Dashboard Data -----
  const fetchDashboard = async () => {
    try {
      const res = await API.get("/dashboard");
      const user = res.data.user;

      setData(res.data);
      setLoadError("");

      if (user) localStorage.setItem("user", JSON.stringify(user));

      if (!user?.plan) {
        setShowPlanPopup(true);
      }

      if (user?.plan && user?.subscriptionActive) {
        const currentPath = window.location.pathname;
        let targetPath = "/dashboard-demo";

        if (user.plan === "basic") {
          targetPath = "/dashboard-basic";
        } else if (user.plan === "premium") {
          targetPath = "/dashboard-premium";
        } else if (user.plan === "elite") {
          targetPath = "/dashboard-elite";
        }

        if (currentPath !== targetPath) {
          setNotice("🎉 Your account has been upgraded! Redirecting...");
          setTimeout(() => {
            navigate(targetPath);
          }, 1500);
        }
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setLoadError(
        `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(() => {
      fetchDashboard();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(
      `https://${theme.referralDomain}/register?ref=${data.user?.referralCode || ""}`
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showComingSoon = (name) => {
    setNotice(`${name} is coming soon!`);
    setTimeout(() => setNotice(""), 2000);
  };

  const handlePlanSelect = async () => {
    if (!selectedPlan) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("User not authenticated");

      await API.post(
        "/dashboard/select-plan",
        { plan: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPlanPopup(false);
      fetchDashboard();
    } catch (err) {
      console.error("Plan selection error:", err);
      alert(err.response?.data?.message || "Failed to save plan");
    }
  };

  if (!data) {
    return (
      <div className={`flex items-center justify-center min-h-screen bg-gradient-to-br ${theme.bgGradient} p-6`}>
        <div className="text-center max-w-md">
          <p className="text-white text-xl animate-pulse mb-4">Loading dashboard...</p>
          {loadError && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs text-left break-words">
              🔬 Debug: {loadError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (data.user?.accountLocked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-500">Account Locked</h2>
          <p className="mt-2">{data.user.accountLockReason}</p>
        </div>
      </div>
    );
  }

  const bankWallets = theme.wallets.map(w => ({
    ...w,
    balance: data.user?.balance || 0,
  }));

  const cryptoWallets = [
    { name: "Binance", logo: binanceLogo, active: false },
    { name: "Bybit", logo: bybitLogo, active: false },
    { name: "Luno", logo: lunoLogo, active: true, link: "/luno-dashboard" },
    { name: "PayPal", logo: paypalLogo, active: false },
  ];

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-b ${theme.bgGradient} text-white p-6 md:p-10`}>

      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3 flex-wrap">
            {isGhana ? "Akwaaba," : "Hello,"}{" "}
            <span className={theme.nameColor}>{data.user?.name || "User"}</span>
            {!data.user?.subscriptionActive && (
              <span className="px-2 py-1 text-xs bg-yellow-500 text-black rounded-full">Free Account</span>
            )}
          </h2>

          {telegramBannerVisible && (
            <TelegramBotBanner theme={theme} onDismiss={() => setTelegramBannerVisible(false)} />
          )}

          {data.user?.accountLocked && (
            <div className="bg-red-600 text-white p-3 rounded-lg mt-4">
              🔒 Account Locked: {data.user.accountLockReason}
            </div>
          )}

          {data.user?.isRestricted && (
            <div className="bg-yellow-500 text-black p-3 rounded-lg mt-4">
              ⚠️ Restricted: {data.user.customMessage || "Your account is limited"}
            </div>
          )}

          {data.user?.isUnderReview && (
            <div className="bg-blue-600 text-white p-3 rounded-lg mt-4">
              🧐 Your account is under review
            </div>
          )}

          <p className="text-sm text-gray-300 mt-1">
            Welcome to your {theme.appName} dashboard — everything you need in one place.
          </p>

          <div className="mt-6 bg-black/40 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-300">Notifications</h3>

            {data.user?.notifications?.length > 0 ? (
              data.user.notifications.slice(0, 3).map((n, i) => (
                <div key={i} className="text-xs text-gray-400 mt-2">
                  • {n.message}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 mt-2">No notifications</p>
            )}
          </div>

          {/* Action Buttons — Talk to Support no longer here */}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/purchase")}
              className={`px-4 py-2 rounded-full font-semibold bg-gradient-to-r ${theme.addMoneyBtn} transform transition`}
            >
              + Add Money
            </button>

            <button
              disabled={!data.user?.canWithdraw}
              onClick={() => navigate("/withdraw")}
              className="px-4 py-2 rounded-full font-semibold bg-green-600 disabled:bg-gray-500"
            >
              Withdraw
            </button>

            {data.user?.hasEverFunded === true && data.user?.subscriptionActive !== true && (
              <button
                onClick={() => navigate("/subscription")}
                className={`px-4 py-2 rounded-full font-semibold bg-gradient-to-r ${theme.subscribeBtn} transform transition`}
              >
                Subscribe
              </button>
            )}
          </div>

          <div className="mt-4 bg-gradient-to-r from-slate-800 to-black/40 p-4 rounded-lg w-full max-w-md space-y-3">
            <div className="text-xs text-gray-300 space-y-1">
              <div>
                Referrals:{" "}
                <span className="text-yellow-300 font-semibold">
                  {data.user?.referralCount || 0}
                </span>
              </div>

              <div>
                Referral Bonus:{" "}
                <span className="text-green-400 font-semibold">
                  {theme.currencySymbol}{data.user?.referralEarnings || 0}
                </span>
              </div>

              <div>
                Your Code:{" "}
                <span className="text-pink-300 font-mono">
                  {data.user?.referralCode || "N/A"}
                </span>
              </div>
            </div>

            <div className="text-xs text-gray-400 break-all">
              {`https://${theme.referralDomain}/register?ref=${data.user?.referralCode || ""}`}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className={`flex-1 py-1 text-sm rounded-md bg-gradient-to-r ${theme.primaryGradient} hover:opacity-90 transition text-black font-semibold`}
              >
                Copy
              </button>
            </div>

            {copied && (
              <div className="text-xs text-green-300">Copied!</div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <div className="text-right">
              <div className="text-xs text-gray-400">Balance</div>
              <div className={`font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r ${theme.balanceGradient}`}>
                {theme.currencySymbol}{data.user?.balance?.toLocaleString() || "0"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-400 mt-1">
        Withdrawable: {theme.currencySymbol}{data.user?.withdrawableBalance?.toLocaleString() || 0}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 flex flex-col gap-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="p-6 rounded-2xl bg-black/50 backdrop-blur-lg border border-white/5 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">{isGhana ? "Mobile Money & Banks" : "Wallets"}</h3>
                <p className="text-xs text-gray-400">
                  {isGhana ? "Access your MoMo & bank wallets quickly" : "Access your bank & crypto wallets quickly"}
                </p>
              </div>
              <div className="text-xs text-gray-400">
                Updated: <span className={theme.accentText}>Just now</span>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bankWallets.map((wallet, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.05 }}
                  className="p-3 rounded-xl flex flex-col items-center justify-center bg-gradient-to-br from-black/60 to-black/30 border border-white/5 shadow-md"
                  onClick={() => (wallet.active && wallet.link ? navigate(wallet.link) : showComingSoon(wallet.name))}
                >
                  <div className={`w-14 h-14 rounded-full ${wallet.color ? `bg-gradient-to-br ${wallet.color}` : "bg-white/5"} flex items-center justify-center overflow-hidden`}>
                    {wallet.logo ? (
                      <img src={wallet.logo} alt={wallet.name} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-2xl">{wallet.emoji}</span>
                    )}
                  </div>
                  <div className={`mt-2 text-sm font-semibold ${theme.accentText}`}>{wallet.name}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {theme.currencySymbol}{(wallet.balance || 0).toLocaleString()}
                  </div>
                </motion.button>
              ))}
            </div>

            <hr className="my-5 border-white/5" />
            <div>
              <h4 className="text-sm font-semibold text-gray-300">Crypto Wallets</h4>
              <div className="mt-3 flex gap-3 flex-wrap">
                {cryptoWallets.map((wallet, i) => (
                  <button
                    key={i}
                    onClick={() => (wallet.active && wallet.link ? navigate(wallet.link) : showComingSoon(wallet.name))}
                    className="flex items-center gap-3 p-2 rounded-lg bg-black/40 border border-white/5 shadow-sm transition hover:scale-105"
                  >
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-white/5 flex items-center justify-center">
                      <img src={wallet.logo} alt={wallet.name} className="w-full h-full object-contain" />
                    </div>
                    <div className={`text-sm ${theme.accentText}`}>{wallet.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        <aside className="flex flex-col gap-6">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="p-4 rounded-2xl bg-black/50 border border-white/5 shadow-lg w-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-400">Activity</div>
                <div className={`font-semibold ${theme.accentText}`}>Latest transactions</div>
              </div>
              <div className="text-xs text-gray-400 cursor-pointer">See all</div>
            </div>
            <div className="mt-3 space-y-3">
              {(data.transactions || []).slice(0, 4).map((tx, i) => (
                <div key={i} className="flex items-center justify-between text-sm text-gray-300">
                  <div className="truncate">{tx.description || "Transaction"}</div>
                  <div className={`font-medium ${tx.amount > 0 ? "text-green-300" : "text-red-400"}`}>
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </div>
                </div>
              ))}
              {(!data.transactions || data.transactions.length === 0) && (
                <div className="text-xs text-gray-500">No recent activity</div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="p-4 rounded-2xl bg-black/50 border border-white/5 shadow-lg w-full"
          >
            {data.user?.isVendor ? (
              <div className="space-y-3">
                <div className="text-sm text-gray-300">Vendor</div>
                <div className={`font-semibold ${theme.accentText}`}>Your vendor dashboard is live</div>
                <button
                  onClick={() => navigate("/vendor")}
                  className={`w-full py-2 mt-2 rounded-md bg-gradient-to-r ${theme.secondaryGradient} text-white hover:brightness-110 transition`}
                >
                  Go to vendor
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-gray-300">Become a Vendor</div>
                <div className={`font-semibold ${theme.accentText}`}>Start selling with {theme.appName}</div>
                <button
                  onClick={() => navigate("/become-vendor")}
                  className="w-full py-2 mt-2 rounded-md bg-gradient-to-r from-pink-500 to-pink-600 hover:brightness-110 transition"
                >
                  Apply now
                </button>
              </div>
            )}
          </motion.div>

          {/* ✅ Referral withdrawal card */}
          <ReferralWithdrawalCard
            currencySymbol={theme.currencySymbol}
            theme={{
              card: "bg-black/50 border border-white/5",
              accent: `bg-gradient-to-r ${theme.primaryGradient} text-black`,
            }}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="p-4 rounded-2xl bg-gradient-to-br from-indigo-900/40 to-black/30 border border-white/5 shadow-lg w-full"
          >
            <div className="text-xs text-gray-400">Tips</div>
            <div className="text-sm text-gray-200 mt-2">
              Complete your profile to unlock rewards and higher transfer limits.
            </div>
          </motion.div>
        </aside>
      </div>

      {notice && <FloatingNotice message={notice} />}

      {/* Floating Talk to Support button — bottom-right, replacing ChatWidget's round icon */}
      <TawkFloatingButton />

      {showPlanPopup && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="bg-[#020617] p-8 rounded-2xl border border-white/10 w-full max-w-sm text-center">
            <h2 className={`text-xl font-bold ${theme.accentText} mb-3`}>Choose Your Account Plan</h2>
            <p className="text-sm text-gray-400 mb-6">Select a plan to continue using your dashboard.</p>
            <div className="flex flex-col gap-3">
              {["basic", "premium", "elite"].map((plan) => (
                <button
                  key={plan}
                  onClick={() => setSelectedPlan(plan)}
                  className={`p-3 rounded-lg border transition ${
                    selectedPlan === plan
                      ? `bg-gradient-to-r ${theme.primaryGradient} text-black font-semibold`
                      : "border-white/10 hover:bg-white/5"
                  }`}
                >
                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                </button>
              ))}
              <button
                onClick={handlePlanSelect}
                disabled={!selectedPlan}
                className={`mt-4 py-2 rounded-lg bg-gradient-to-r ${theme.primaryGradient} text-black font-semibold disabled:opacity-40`}
              >
                Confirm Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
    }
